import SessaoServico from "@/servicos/sessao.service"
import { Grupos, PerfilDeOrganizacao } from "@/types/gerais"
import { normalizarPermissoes, possuiPermissao, temPermissao } from "@/store/acesso"
import {
  GrupoDePermissoes,
  modulosDePermissao,
  OperacaoDePermissionamento,
  Permissao,
} from "./modulosDePermissao"
/**
 * tipo do composable usarPermissionamento
 */
export type ComposableDePermissionamento = {
  verificarPermissao: (operacao: OperacaoDePermissionamento) => boolean
  verificarPermissaoIntrinseca: (
    operacao: OperacaoDePermissionamento,
    recurso: RecursoDePermissionamento,
    chave?: IdentificadorDeRecurso
  ) => boolean;
  verificarPermissaoPorGrupo: (
    operacao: OperacaoDePermissionamento,
    grupo: Grupos
  ) => boolean
}
/**
 * Determina o ator e seu perfil organizacional, que juntos determinam qual será seu nível permissionamento
 */
export type AtorDePermissionamento = {
  id: string;
  perfilDeOrganizacao: PerfilDeOrganizacao;
  email?: string;
};
/**
 * Identificador único de um recurso
 */
type IdentificadorDeRecurso = "id" | "email"
export type RecursoDePermissionamento = {
  id?: string;
  email?: string;
} & ({ id: string } | { email: string })

export type NomeModuloTimesheet =
  | "Usuarios"
  | "Projetos"
  | "Recursos"
  | "TiposDeRecursos"
  | "Profissionais"
  | "CategoriasDeProfissionais"
  | "Atributos"
  | "TiposDeAtividade"
  | "AtividadesRealizadas"
  | "AtividadesPlanejadas"
  | "Ocorrencias"
  | "Eventos"
  | "Travamento"
  | "Importacoes"

type AcaoApiDePermissao = "get" | "post" | "delete"
type EscopoApiDePermissao = "mine" | "group" | "all"

const modulosApiPorModuloFrontend: Partial<Record<NomeModuloTimesheet, string[]>> = {
  Usuarios: ["usuario"],
  Projetos: ["projeto", "grupo_de_projetos"],
  Profissionais: ["profissional"],
  Atributos: ["atributo"],
  TiposDeAtividade: ["tipo_de_atividade"],
  AtividadesPlanejadas: [
    "atividade_planejada",
    "atividade_do_projeto",
    "calendario_da_atividade",
  ],
  AtividadesRealizadas: ["atividade_realizada", "marcacao"],
  Ocorrencias: ["atividade_realizada"],
  Eventos: ["atividade_planejada", "calendario_da_atividade"],
  Travamento: ["travamento"],
  Importacoes: ["importacao"],
}

const acoesApiPorOperacao: Record<OperacaoDePermissionamento, AcaoApiDePermissao> = {
  listar: "get",
  visualizar: "get",
  criar: "post",
  iniciar: "post",
  finalizar: "post",
  editar: "post",
  deletar: "delete",
  arquivar: "post",
  desarquivar: "post",
  travar: "post",
  destravar: "post",
  ativar: "post",
  desativar: "post",
}

const acoesApiPorModulo: Partial<
  Record<NomeModuloTimesheet, Partial<Record<OperacaoDePermissionamento, AcaoApiDePermissao>>>
> = {
  Importacoes: { listar: "post", visualizar: "post" },
}

const escoposApiPorGrupo: Record<Exclude<Grupos, "" | undefined>, EscopoApiDePermissao[]> = {
  proprio: ["mine"],
  outros: ["group", "all"],
  ambos: ["mine", "group", "all"],
}

const extrairAtorEmUso = (
  atorExterno?: AtorDePermissionamento
): AtorDePermissionamento => {
  return (
    atorExterno ||
    (SessaoServico.coletarInformacoesDoUsuarioLogado() as AtorDePermissionamento)
  )
}

const extrairPermissoesGarantidas = (
  nomeDoModulo: NomeModuloTimesheet,
  atorEmUso: AtorDePermissionamento
): Permissao[] => {
  const moduloEmUso: GrupoDePermissoes = modulosDePermissao[nomeDoModulo]
  if (!moduloEmUso) {
    return []
  }
  if (atorEmUso && atorEmUso.perfilDeOrganizacao && atorEmUso.perfilDeOrganizacao in moduloEmUso) {
    return moduloEmUso[atorEmUso.perfilDeOrganizacao]
  }
  return moduloEmUso.administrador || []
}

/**
 *
 * @param {NomeModuloTimesheet} nomeDoModulo - nome de um dos módulos do sistema que pode ser permissionado
 * @param {AtorDePermissionamento} atorExterno - objeto contendo informações como perfil de organização e id do ator
 * @returns {
 *  (operacao: OperacaoDePermissionamento): boolean,
 *  (operacao: OperacaoDePermissionamento, recurso: RecursoDePermissionamento): boolean
 * } - retorna funções que recebem a operação e recurso sobre o qual deseja validar a permissão
 */
export function usarPermissionamento(
  nomeDoModulo: NomeModuloTimesheet,
  atorExterno?: AtorDePermissionamento
): ComposableDePermissionamento {
  const atorEmUso = extrairAtorEmUso(atorExterno)
  const permissoesGarantidas = extrairPermissoesGarantidas(
    nomeDoModulo,
    atorEmUso
  )

  const buscarPermissao = (operacao: OperacaoDePermissionamento) => {
    return permissoesGarantidas.find(
      (permissao) => permissao.operacao === operacao
    )
  }

  const verificarPermissaoApi = (
    operacao: OperacaoDePermissionamento,
    escopos: EscopoApiDePermissao[] = ["mine", "group", "all"]
  ): boolean => {
    const modulosApi = modulosApiPorModuloFrontend[nomeDoModulo] || []
    const acaoApi =
      acoesApiPorModulo[nomeDoModulo]?.[operacao] || acoesApiPorOperacao[operacao]
    const permissoes = SessaoServico.coletarPermissoes()
    return modulosApi.some((modulo) =>
      possuiPermissao(permissoes, modulo, acaoApi, escopos)
    )
  }
  /**
   * Verificar se o ator tem permissão de realizar a operação passada por parametro
   *
   * @param {OperacaoDePermissionamento} operacao - são os tipos de permissão existentes
   * @returns verdadeiro se tem permissão ou falso, caso contrário
   * @memberof usarPermissionamento
   */
  const verificarPermissao = (
    operacao: OperacaoDePermissionamento
  ): boolean => {
    if (SessaoServico.usaPermissionamentoGranular) {
      return verificarPermissaoApi(operacao)
    }
    const permissoesUsuario = SessaoServico.coletarPermissoes()
    if (permissoesUsuario.length > 0) {
      const normalizadas = normalizarPermissoes(permissoesUsuario)
      const mod = nomeDoModulo.toLowerCase()
      const op = operacao.toLowerCase()
      const acoesEquivalentes: Record<string, string[]> = {
        listar: ["listar", "get"],
        visualizar: ["visualizar", "get"],
        criar: ["criar", "post"],
        editar: ["editar", "patch", "put"],
        deletar: ["deletar", "delete"],
        ativar: ["ativar", "post"],
        desativar: ["desativar", "post"],
      }
      const acoes = acoesEquivalentes[op] || [op]
      for (const acao of acoes) {
        if (temPermissao(normalizadas, { modulo: mod, acao })) {
          return true
        }
        if (temPermissao(normalizadas, `${mod}_${acao}`)) {
          return true
        }
      }
      if (temPermissao(normalizadas, op) || temPermissao(normalizadas, "*")) {
        return true
      }
    }

    const permissaoEncontrada = buscarPermissao(operacao)
    if (!permissaoEncontrada) {
      return false
    }

    return permissaoEncontrada.verificarAutoNegacao(!!permissaoEncontrada)
  }
  /**
   * Verificar se o ator tem permissão de realizar a operação passada por parametro
   *
   * @param {OperacaoDePermissionamento} operacao - são os tipos de permissão existentes
   * @param {AcaoDePermissionamento} operacao - são os tipos de permissão existentes
   * @param {GrupoDePermissoes} intrinseco - se a permissão é direcionado a recursos próprios ou aos dos outros
   * @returns verdadeiro se tem permissão ou falso, caso contrário
   * @memberof usarPermissionamento
   */
  const verificarPermissaoPorGrupo = (
    operacao: OperacaoDePermissionamento,
    grupo: Grupos = "outros"
  ) => {
    if (SessaoServico.usaPermissionamentoGranular) {
      const escopos: EscopoApiDePermissao[] = grupo && grupo in escoposApiPorGrupo
        ? escoposApiPorGrupo[grupo as Exclude<Grupos, "" | undefined>]
        : ["mine", "group", "all"]
      return verificarPermissaoApi(operacao, escopos)
    }
    if (grupo === "ambos") {
      return verificarPermissao(operacao)
    }

    const ehIntrinseco: boolean = grupo === "proprio"
    const permissaoEncontrada = buscarPermissao(operacao)
    if (!permissaoEncontrada) {
      return false
    }
    return (
      permissaoEncontrada.intriseca === ehIntrinseco &&
      permissaoEncontrada.verificarAutoNegacao(!!permissaoEncontrada)
    )
  }

  /**
   * Verificar se o ator tem permissão de realizar a operação sobre um determinado recurso
   *
   * @param {OperacaoDePermissionamento} operacao - são os tipos de permissão existentes
   * @param {RecursoDePermissionamento} recurso - identificação do recurso
   * @returns verdadeiro se tem permissão ou falso, caso contrário
   * @memberof usarPermissionamento
   */
  const verificarPermissaoIntrinseca = (
    operacao: OperacaoDePermissionamento,
    recurso: RecursoDePermissionamento,
    identificadorDeRecurso: IdentificadorDeRecurso = "id"
  ): boolean => {
    if (SessaoServico.usaPermissionamentoGranular) {
      const usuarioPossuiRecurso =
        atorEmUso?.[identificadorDeRecurso] === recurso[identificadorDeRecurso]
      return (
        usuarioPossuiRecurso &&
        verificarPermissaoApi(operacao, ["mine"])
      )
    }
    const permissaoEncontrada = permissoesGarantidas.find(
      (permissao) => permissao.intriseca && permissao.operacao === operacao
    )
    if (!permissaoEncontrada) { return false }
    const recursoIntrinsecoEmRelacaoAoAtor = atorEmUso[identificadorDeRecurso] === recurso[identificadorDeRecurso]
    return permissaoEncontrada.verificarAutoNegacao(
      permissaoEncontrada.verificarExigenciaDeRelacionamentoIntrinseco(
        recursoIntrinsecoEmRelacaoAoAtor
      )
    )
  }

  return {
    verificarPermissao,
    verificarPermissaoIntrinseca,
    verificarPermissaoPorGrupo,
  }
}
